import { Box, createStyles, Group, Stack } from '@mantine/core';
import type { GroupItemSettingSchema } from '@repo/api-codegen';
import {
	useApiRefreshIntegrationGroupItems,
	useApiUpdateIntegrationGroupSettings,
	useSuspenseApiGetIntegrationGroupItems,
	useSuspenseApiGroupItemsRefreshSupport,
} from '@repo/api-codegen';
import { memo, Suspense, useCallback, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';

import { showNotification } from '@mantine/notifications';
import {
	useApiUpdateIntegrationGroupTeamsAssigned,
	useApiUpdateIntegrationGroupVisibility,
} from '@repo/api-codegen/v2.ts';
import type { IntegrationSpec } from '@repo/common/interfaces/integrationSpec.ts';
import type { IIntegration } from '@repo/common/models/integration.ts';
import { useMobxReaction } from '@repo/common/utils/useMobxReaction.ts';
import { Button, Icon } from '@repo/foundations';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import type { IBackgroundJob } from '../../api/types/models/backgroundJob.ts';
import { BackgroundJobProgress } from '../../components/BackgroundJobProgress/BackgroundJobProgress.tsx';
import SearchBox from '../../components/SearchBox/SearchBox.tsx';
import type { BackgroundJob } from '../../lib/models/backgroundJob.ts';
import { useFeatureFlags } from '../../utils/featureFlags/useFeatureFlags.ts';
import { SelectAllGroupsCheckbox } from './components/SelectAllGroupsCheckbox.tsx';
import { IntegrationGroupsSkeleton } from './components/SelectIntegrationGroupsPanelSkeleton.tsx';
import { SelectIntegrationGroupsPanelTableRow } from './components/SelectIntegrationGroupsTableRow.tsx';
import { SelectIntegrationGroupsPanelTableRowV2 } from './components/SelectIntegrationGroupsTableRowV2.tsx';
import { SettingsStore } from './store';

const useStyles = createStyles((theme) => ({
	table: {
		borderCollapse: 'separate',
		border: `1px solid ${theme.other.getColor('border/primary/default')}`,
		borderRadius: theme.radius.md,
		overflow: 'hidden',
		width: '100%',
		height: '100%',
		tableLayout: 'fixed',
		gap: 0,

		'[role="row"]': {
			gap: 0,

			'&:first-of-type': {
				backgroundColor: theme.other.getColor('surface/secondary/default'),
				borderBottom: `1px solid ${theme.other.getColor('border/secondary/default')}`,
			},
		},

		'[role="columnheader"]': {
			padding: theme.spacing.xs,
			fontSize: theme.other.typography.text.sm,
			fontWeight: theme.other.typography.weight.semibold,
			color: theme.other.getColor('text/secondary/default'),

			'&:first-of-type': {
				width: '60%',
				paddingLeft: theme.spacing.md,
			},
		},
		'[role="cell"]': {
			padding: theme.spacing.xs,
			fontSize: theme.other.typography.text.xs,

			'&:first-of-type': {
				width: '60%',
				paddingLeft: theme.spacing.md,
				fontWeight: theme.other.typography.weight.bold,
			},
		},
	},
}));

interface SelectPanelProps {
	integration: IIntegration;
	spec: IntegrationSpec;
	refreshOnLoad?: boolean;
	height?: string;
}

const InternalSelectIntegrationGroupsController = observer(
	({
		integration,
		spec,
		refreshOnLoad = false,
		height = '100vh',
	}: SelectPanelProps) => {
		const { classes } = useStyles();
		const { teamsPermissionsV2Frontend } = useFeatureFlags();

		// Check support refresh
		const { data: supportRefresh } = useSuspenseApiGroupItemsRefreshSupport({
			queryParams: {
				integration_type: integration.type,
			},
		});

		// Get cached values
		const { data: groupItems, refetch } =
			useSuspenseApiGetIntegrationGroupItems({
				pathParams: {
					integrationId: integration.id,
				},
			});

		const [store] = useState(() => new SettingsStore(spec.type, groupItems));

		const {
			mutateAsync: apiRefreshIntegrationGroupItems,
			reset: clearBackgroundJob,
		} = useApiRefreshIntegrationGroupItems({});

		const handleBackgroundJobComplete = useCallback(
			(job?: IBackgroundJob) => {
				if (!job) return;
				if (job?.failed) {
					showNotification({
						title: 'Error refreshing schema',
						message:
							'An error has occurred while refreshing integration schema',
						color: 'red',
						icon: (
							<Icon name="alertCircle" color="text/critical-on-fill/default" />
						),
					});
				} else {
					showNotification({
						title: 'Schema refreshed successfully',
						message: 'Schema ran successfully',
						color: 'green',
						icon: <Icon name="check" color="text/success-on-fill/default" />,
					});
				}

				clearBackgroundJob();
				store.resetBackgroundJob();
				refetch().then((res) => {
					if (res.data) {
						store.setInitialSettings(res.data);
					}
				});
			},
			[clearBackgroundJob, refetch, store]
		);

		const handleRefreshGroupItems = useCallback(async () => {
			const backgroundJob = await apiRefreshIntegrationGroupItems({
				pathParams: {
					integrationId: integration.id,
				},
			});
			store.setBackgroundJob(backgroundJob);
		}, [apiRefreshIntegrationGroupItems, integration.id, store]);

		useMount(() => {
			if (refreshOnLoad) {
				handleRefreshGroupItems();
			}
		});

		// Update group settings
		const { mutateAsync: apiUpdateIntegrationGroupSettings } =
			useApiUpdateIntegrationGroupSettings({
				meta: {
					normalize: false,
				},
			});

		const itemContent = useCallback(
			(_: number, item: GroupItemSettingSchema) =>
				teamsPermissionsV2Frontend ? (
					<SelectIntegrationGroupsPanelTableRowV2
						key={item.databuilder_id}
						databuilderId={item.databuilder_id}
						filteredItem={item}
						store={store}
						inheritedValues={integration.teams}
						inheritedValuesTooltip="Inherited from the integration settings"
					/>
				) : (
					<SelectIntegrationGroupsPanelTableRow
						key={item.databuilder_id}
						databuilderId={item.databuilder_id}
						filteredItem={item}
						store={store}
					/>
				),
			[store, integration.teams, teamsPermissionsV2Frontend]
		);

		// Persist settings to backend
		const { mutateAsync: apiUpdateIntegrationGroupVisibility } =
			useApiUpdateIntegrationGroupVisibility({});

		const { mutateAsync: apiUpdateIntegrationGroupTeamsAssigned } =
			useApiUpdateIntegrationGroupTeamsAssigned({});

		useMobxReaction(
			useCallback(
				() =>
					teamsPermissionsV2Frontend
						? Object.keys(store.visibilityUpdates).length
						: 0,
				[store, teamsPermissionsV2Frontend]
			),
			useCallback(async () => {
				if (!teamsPermissionsV2Frontend) {
					return;
				}
				if (Object.keys(store.visibilityUpdates).length === 0) {
					return;
				}
				await apiUpdateIntegrationGroupVisibility({
					body: {
						updates: store.visibilityUpdates,
					},
					pathParams: {
						integrationId: integration.id,
					},
				});
				store.resetVisibilityUpdates();
			}, [
				store,
				apiUpdateIntegrationGroupVisibility,
				integration.id,
				teamsPermissionsV2Frontend,
			])
		);

		useMobxReaction(
			useCallback(
				() =>
					teamsPermissionsV2Frontend
						? Object.keys(store.teamsAssignedUpdates).length
						: 0,
				[store, teamsPermissionsV2Frontend]
			),
			useCallback(async () => {
				if (!teamsPermissionsV2Frontend) {
					return;
				}
				if (Object.keys(store.teamsAssignedUpdates).length === 0) {
					return;
				}
				await apiUpdateIntegrationGroupTeamsAssigned({
					body: {
						updates: store.teamsAssignedUpdates,
					},
					pathParams: {
						integrationId: integration.id,
					},
				})
					.then(() => {
						showNotification({
							message: 'Team changes may take a few minutes to propagate.',
							color: 'blue',
						});
						store.resetTeamsAssignedUpdates();
					})
					.catch((error) => {
						showNotification({
							title: 'Error updating group settings',
							message: error.message,
							color: 'red',
						});
						store.resetTeamsAssignedUpdates();
					});
			}, [
				store,
				apiUpdateIntegrationGroupTeamsAssigned,
				integration.id,
				teamsPermissionsV2Frontend,
			])
		);

		useMobxReaction(
			useCallback(
				() =>
					!teamsPermissionsV2Frontend
						? Object.keys(store.settingDiff).length
						: 0,
				[store, teamsPermissionsV2Frontend]
			),
			useCallback(async () => {
				if (teamsPermissionsV2Frontend) {
					return;
				}
				if (Object.keys(store.settingDiff).length === 0) {
					return;
				}
				const oldState = Object.keys(store.settingDiff).reduce(
					(acc, key) => {
						acc[key] = store.settingsMap[key];
						return acc;
					},
					{} as Record<string, GroupItemSettingSchema>
				);
				const diffCopy = JSON.parse(JSON.stringify(store.settingDiff));
				store.resetDiff();

				await apiUpdateIntegrationGroupSettings({
					body: {
						group_settings: diffCopy,
					},
					pathParams: {
						integrationId: integration.id,
					},
				}).catch((error) => {
					showNotification({
						title: 'Error updating group settings',
						message: error.message,
						color: 'red',
					});
					// Rollback
					for (const key in oldState) {
						store.settingsMap[key] = oldState[key];
					}
				});
			}, [store, apiUpdateIntegrationGroupSettings, integration.id])
		);

		return (
			<>
				<Stack spacing="sm" style={{ minHeight: 0 }}>
					<Group spacing="sm" noWrap>
						<SearchBox
							variant="tertiary"
							placeholder="Search"
							onSearch={store.setSearchTerm}
						/>
						{supportRefresh && (
							<Button
								variant="default"
								size="md"
								leftIconName="refresh"
								loading={!!store.backgroundJob}
								disabled={!!store.backgroundJob}
								onClick={handleRefreshGroupItems}
							>
								Refresh
							</Button>
						)}
					</Group>
					<Stack h={'100%'} pos="relative" style={{ overflowY: 'auto' }}>
						<Stack role="table" className={classes.table}>
							<Group role="row">
								<Box role="columnheader">
									<Group spacing="md">
										<SelectAllGroupsCheckbox store={store} />
										Name
									</Group>
								</Box>
								<Box role="columnheader">Teams</Box>
							</Group>
							<Virtuoso
								style={{ height }}
								data={store.filteredSettings}
								itemContent={itemContent}
							/>
						</Stack>
					</Stack>
				</Stack>
				{store.backgroundJob && (
					<BackgroundJobProgress
						key={store.backgroundJob.id}
						job={store.backgroundJob as unknown as BackgroundJob}
						onCompleted={handleBackgroundJobComplete}
					/>
				)}
			</>
		);
	}
);

export const SelectIntegrationGroupsController = memo(
	(props: SelectPanelProps) => (
		<Suspense fallback={<IntegrationGroupsSkeleton />}>
			<InternalSelectIntegrationGroupsController {...props} />
		</Suspense>
	)
);
